import { HttpMethod, HttpRequestUtility } from '@chorus/json-request-library';
import { Communication } from '../Modules/GdprModule';
import { HowHeardAboutUsOption } from '../Modules/HowHeardAboutUsModule';
import { IbanPaymentDetails } from '../Types/Payments/IbanPaymentDetails';
import { IDebitCardDetails } from '../Types/Payments/IDebitCardDetails';
import RejectionHandler from './RejectionHandler';

type ApiCommunicationType = 'email' | 'phone' | 'post' | 'sms';

interface ProductDetail {
  id: string;
  priceInPence: number;
}

interface GroupMemberDetails {
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  telephone: string;
  dateOfBirth: string;
  address: {
    line1: string;
    line2: string;
    line3: string;
    county: string;
    city: string;
    postCode: string;
    country: string;
    countryCode: string;
  };
}

interface ApiDirectDebitDetails {
  paymentDay: string;
  paymentFrequencyInMonths: string;
}

type ApiLocalAccountDirectDebitDetails = {
  accountNumber: string;
  sortCode: string;
} & ApiDirectDebitDetails;

type ApiIbanDirectDebitDetails = {
  iban: string;
} & ApiDirectDebitDetails;

interface ApiCommunicationPreference {
  communicationPreferenceId: string;
  communicationPreferenceType: string;
  optedIn: boolean;
}

interface BankApiMakeDonationRequest {
  paidBy: {
    title: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    telephone: string;
    dateOfBirth?: string;
    address: {
      line1: string;
      line2: string;
      line3: string;
      county: string;
      city: string;
      postCode: string;
      country: string;
      countryCode: string;
    };
    giftAidOptedIn?: boolean;
    communications?: ApiCommunicationPreference[];
    channels?: ApiCommunicationType[];
  };
  heldBy: {
    title: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    telephone: string;
    dateOfBirth?: string;
    address: {
      line1: string;
      line2: string;
      line3: string;
      county: string;
      city: string;
      postCode: string;
      country: string;
      countryCode: string;
    };
  };
  communicationRecipient: string;
  products: ProductDetail[];
  directDebitPaymentDetails: ApiLocalAccountDirectDebitDetails | ApiIbanDirectDebitDetails;
  projectId?: string;

  mapToCampaign?: 'HowHeardAboutUs' | 'ReasonForDonating';

  donationTypeId?: string;
  donationTypeOther?: string;

  howHeardAboutUs?: {
    howHeardAboutUsOptionId?: string;
    otherOptionText?: string;
  };
  groupMembers?: GroupMemberDetails[];
  promoCode?: string;
  startDate?: Date;
  comments: string;
  DefaultTheEndDate: boolean | null;
}

interface DonorDetails {
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  telephone: string;
  organisation: string;
  address: {
    postcode: string;
    line1: string;
    line2: string;
    line3: string;
    town: string;
    county: string;
    country: string;
    countryIso: string;
  };
}

class DirectDebitRespository {

  static CreateBankPayment = (
    evoApiBaseUrl: string,
    donationDetails: {
      productId: string;
      annualAmountPence: number;
      projectId: string;
      startDate?: Date;
      
    },
    donorDetails: DonorDetails,
    paymentDetails: IDebitCardDetails | IbanPaymentDetails,
    giftAidOptedIn: boolean | undefined,
    communications: Communication[],
    donationTypeDetails: {
      id?: string,
      otherText?: string,
    },
    howHeardAboutUs: {
      options: HowHeardAboutUsOption[],
      otherId: string,
      selectedId: string,
      customValue: string,
    },
    donation: {  comment: string },
    defaultTheEndDate: boolean | null,
    mapToCampaign?: 'HowHeardAboutUs' | 'ReasonForDonating'
  ): Promise<void> => {

    let communicationPreferences: ApiCommunicationPreference[] = [];
    if (communications.length > 0) {

      let groupByCommunications = communications
        .reduce((group: { [key: string]: Communication[] }, item) => {
          if (!group[item.id]) {
            group[item.id] = [];
          }
          group[item.id].push(item);
          return group;
        }, 
                {});

      Object.keys(groupByCommunications).forEach((key) => {
        let channels: ApiCommunicationType[] = [];
        groupByCommunications[key].forEach((preference: Communication) => {
          if (preference.email !== undefined && preference.email) {
            channels.push('email');
          }

          if (preference.phone !== undefined && preference.phone) {
            channels.push('phone');
          }

          if (preference.post !== undefined && preference.post) {
            channels.push('post');
          }

          if (preference.sms !== undefined && preference.sms) {
            channels.push('sms');
          }
        });

        if (channels.length > 0) {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: channels.join(','),
            optedIn: true
          });
        } else {
          communicationPreferences.push({
            communicationPreferenceId: key,
            communicationPreferenceType: 'email,post,sms,phone',
            optedIn: false
          });
        }

      });
    }

    let howHeardAboutUsText;

    if (howHeardAboutUs.selectedId === howHeardAboutUs.otherId) {
      howHeardAboutUsText = howHeardAboutUs.customValue;
    } else {
      const selectedOption = howHeardAboutUs.options.find((v) => v.id === howHeardAboutUs.selectedId);

      if (selectedOption) {
        howHeardAboutUsText = selectedOption.name;
      }
    }

    let ddPaymentDetails = DirectDebitRespository.makeAccountDetails(paymentDetails);

    let mappedCampaignActivity;
    let mappedComment;
    switch (mapToCampaign) {
      case 'ReasonForDonating':
        mappedCampaignActivity = {
          howHeardAboutUsOptionId: donationTypeDetails.id,
          otherOptionText: donationTypeDetails.otherText
        };
        mappedComment = howHeardAboutUsText ? `How Heard About Us: ${howHeardAboutUsText}` : null;
        break;
      default:
        mappedCampaignActivity = howHeardAboutUs.selectedId === '' ? undefined : {
          howHeardAboutUsOptionId: howHeardAboutUs.selectedId,
          otherOptionText: howHeardAboutUsText
        };
        mappedComment = donationTypeDetails.otherText ? `Donation Type: ${donationTypeDetails.otherText}` : null;
    }

    let requestBody: BankApiMakeDonationRequest = {
      paidBy: {
        title: donorDetails.title,
        firstName: donorDetails.firstName,
        lastName: donorDetails.lastName,
        emailAddress: donorDetails.emailAddress,
        telephone: donorDetails.telephone,
        dateOfBirth: '',
        address: {
          line1: donorDetails.address.line1,
          line2: donorDetails.address.line2,
          line3: donorDetails.address.line3,
          county: donorDetails.address.county,
          city: donorDetails.address.town,
          postCode: donorDetails.address.postcode,
          country: donorDetails.address.country,
          countryCode: donorDetails.address.countryIso,
        },
        giftAidOptedIn: giftAidOptedIn,
        communications: communicationPreferences
      },

      heldBy: {
        title: donorDetails.title,
        firstName: donorDetails.firstName,
        lastName: donorDetails.lastName,
        emailAddress: donorDetails.emailAddress,
        telephone: donorDetails.telephone,
        dateOfBirth: '',
        address: {
          line1: donorDetails.address.line1,
          line2: donorDetails.address.line2,
          line3: donorDetails.address.line3,
          county: donorDetails.address.county,
          city: donorDetails.address.town,
          postCode: donorDetails.address.postcode,
          country: donorDetails.address.country,
          countryCode: donorDetails.address.countryIso,
        }
      },

      communicationRecipient: 'PaidBy',

      products: [{
        id: donationDetails.productId,
        priceInPence: donationDetails.annualAmountPence
      }],

      directDebitPaymentDetails: ddPaymentDetails,

      projectId: donationDetails.projectId,

      howHeardAboutUs: mappedCampaignActivity,

      groupMembers: [],

      startDate: donationDetails.startDate,
      comments: [
        (mappedComment ? mappedComment : null),
        (donorDetails.organisation ? `Organisation: ${donorDetails.organisation}` : null),
        (donation.comment ? `Comments: ${donation.comment}` : null)
      ].filter(Boolean).join('\n'),
      DefaultTheEndDate: defaultTheEndDate,
    };

    const request = HttpRequestUtility.CreateRequest(
      evoApiBaseUrl + '/api/memberships/directdebit',
      HttpMethod.POST,
      requestBody
    );

    const createPromise = new Promise<void>((resolve, reject) => {
      HttpRequestUtility.ExecuteJsonRequest(request)
        .then(resolve)
        .catch(e => RejectionHandler.handleReject(reject, e as Response));
    });

    return createPromise;
  }

  static makeAccountDetails = (paymentDetails: IDebitCardDetails | IbanPaymentDetails): ApiLocalAccountDirectDebitDetails | ApiIbanDirectDebitDetails => {

    switch (paymentDetails.kind) {
      case 'directdebit': {

        return {
          paymentFrequencyInMonths: '1',
          paymentDay: paymentDetails.paymentDay.toString(),
          accountNumber: paymentDetails.accountNumber,
          sortCode: paymentDetails.sortCode,
        };
      }
      case 'bankaccount': {

        return {
          paymentFrequencyInMonths: '1',
          paymentDay: paymentDetails.dateToDonateInMonth,
          iban: paymentDetails.iban,
        };
      }
      default:
        {
          const _exhaustiveCheck: never = paymentDetails;
        }
    }

    throw 'Unknown Payment Details';
  }
}

export default DirectDebitRespository;



// WEBPACK FOOTER //
// ./src/Helpers/DirectDebitRespository.tsx